import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import segmentsService from "../services/segmentsService";
import campaignService from "../services/campaignService";
import { Segment } from "../types/segment";
import { NewsletterEdition, NewsletterEditionStatus } from "../types/newsletterEdition";
import { selectOrganization } from "../store/slices/authUserSlice";

export interface SendToSegmentModalProps {
  segment: Segment;
  onClose: () => void;
  setSendToSegmentSuccess: (success: boolean) => void;
}

type SelectionMode = "percentage" | "count";

const PERCENTAGE_OPTIONS = [25, 50, 75, 100];

const SendToSegmentModal: React.FC<SendToSegmentModalProps> = ({
  segment,
  onClose,
  setSendToSegmentSuccess,
}) => {
  const organization = useSelector(selectOrganization);
  const [editions, setEditions] = useState<NewsletterEdition[]>([]);
  const [selectedEditionId, setSelectedEditionId] = useState<string>("");
  const [mode, setMode] = useState<SelectionMode>("percentage");
  const [sendPercentage, setSendPercentage] = useState<number>(100);
  const [sendCount, setSendCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>("");

  const subscriberCount = segment.subscriber_count ?? 0;
  const effectiveCount = mode === "percentage"
    ? Math.ceil(subscriberCount * sendPercentage / 100)
    : Math.min(sendCount, subscriberCount);

  useEffect(() => {
    campaignService.listNewsletterEditions().then((all) => {
      const approved = all.filter((e) => e.status === NewsletterEditionStatus.Approved);
      setEditions(approved);
      setLoading(false);
    }).catch(() => {
      setError("Failed to load newsletter editions");
      setLoading(false);
    });
  }, []);

  const handleSend = async () => {
    if (!selectedEditionId) return;
    if (mode === "count" && sendCount <= 0) {
      setError("Subscriber count must be greater than 0");
      return;
    }

    setSending(true);
    setError("");

    try {
      const options = mode === "percentage"
        ? { sendPercentage }
        : { sendCount };
      await segmentsService.sendCampaignToSegment(String(segment.id), selectedEditionId, options);
      onClose();
      setSendToSegmentSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || "Failed to send campaign";
      setError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="primary-input-container"
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        minWidth: "480px",
      }}
    >
      <h2>Send Newsletter to "{segment.name}"</h2>
      {!organization?.feature_flags?.segment_sending_enabled ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#374151" }}>
            Segment sending is not enabled for your account. Contact support to enable it.
          </p>
          <button className="tertiary-button" onClick={onClose}>
            Close
          </button>
        </div>
      ) : loading ? (
        <p>Loading newsletter editions...</p>
      ) : editions.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <p style={{ margin: 0, fontSize: "14px", color: "#374151" }}>
            No approved newsletter editions available. Only editions in "Approved" status can be sent to a segment.
          </p>
          <button className="tertiary-button" onClick={onClose}>
            Close
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <label htmlFor="edition-select">Select an approved newsletter:</label>
          <select
            id="edition-select"
            value={selectedEditionId}
            onChange={(e) => setSelectedEditionId(e.target.value)}
            className="url-input"
          >
            <option value="">-- Choose an edition --</option>
            {editions.map((edition) => (
              <option key={edition.id} value={edition.id}>
                {edition.title || `Edition #${edition.id}`}
              </option>
            ))}
          </select>

          <div>
            <label style={{ display: "block", marginBottom: "8px" }}>
              How many to send to:
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              <button
                className={mode === "percentage" ? "primary-button" : "tertiary-button"}
                style={{ flex: 1, padding: "8px 0" }}
                onClick={() => setMode("percentage")}
                type="button"
              >
                Percentage
              </button>
              <button
                className={mode === "count" ? "primary-button" : "tertiary-button"}
                style={{ flex: 1, padding: "8px 0" }}
                onClick={() => setMode("count")}
                type="button"
              >
                Fixed count
              </button>
            </div>

            {mode === "percentage" ? (
              <div style={{ display: "flex", gap: "8px" }}>
                {PERCENTAGE_OPTIONS.map((pct) => (
                  <button
                    key={pct}
                    className={sendPercentage === pct ? "primary-button" : "tertiary-button"}
                    style={{ flex: 1, padding: "8px 0" }}
                    onClick={() => setSendPercentage(pct)}
                    type="button"
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            ) : (
              <input
                type="number"
                min={1}
                max={subscriberCount || undefined}
                value={sendCount || ""}
                onChange={(e) => setSendCount(parseInt(e.target.value, 10) || 0)}
                placeholder="Enter number of subscribers"
                className="url-input"
                style={{ width: "100%" }}
              />
            )}
          </div>

          <p style={{ margin: 0, fontSize: "14px", color: "#555" }}>
            This will send the selected edition to approximately{" "}
            <strong>{effectiveCount}</strong> of {subscriberCount} subscribers
            in "{segment.name}" immediately.
            Subscribers who have already received this edition will be excluded.
          </p>

          {error && (
            <p style={{ margin: 0, color: "#d32f2f", fontSize: "14px" }}>{error}</p>
          )}

          <button
            className="primary-button"
            onClick={handleSend}
            disabled={!selectedEditionId || sending || (mode === "count" && sendCount <= 0)}
          >
            {sending ? "Sending..." : "Send"}
          </button>
          <button className="tertiary-button" onClick={onClose}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default SendToSegmentModal;
